/**
 * Hook für Content Blocks
 * 
 * CRUD Operationen für Content Blocks.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  getContentBlocks,
  getProductContentBlocks,
  getContentBlock,
  createContentBlock,
  updateContentBlock,
  deleteContentBlock,
} from '@/lib/api/content-blocks'
import type {
  ContentBlocksQuery,
  CreateContentBlockRequest,
  UpdateContentBlockRequest,
  BlockType,
} from '@/types/content-blocks'

/**
 * Hook to fetch content blocks with optional filters
 */
export function useContentBlocks(query?: ContentBlocksQuery) {
  return useQuery({
    queryKey: ['content-blocks', query],
    queryFn: () => getContentBlocks(query),
    staleTime: 30_000,
  })
}

/**
 * Hook to fetch content blocks for a specific product
 */
export function useProductContentBlocks(productId: string | undefined) {
  return useQuery({
    queryKey: ['content-blocks', 'product', productId],
    queryFn: () => getProductContentBlocks(productId!),
    enabled: !!productId,
    staleTime: 30_000,
  })
}

/**
 * Hook to fetch a single content block with versions
 */
export function useContentBlock(blockId: string | undefined) {
  return useQuery({
    queryKey: ['content-block', blockId],
    queryFn: () => getContentBlock(blockId!),
    enabled: !!blockId,
    staleTime: 30_000,
  })
}

/**
 * Hook for creating a content block
 */
export function useCreateContentBlock() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (data: CreateContentBlockRequest) => createContentBlock(data),
    onSuccess: (data) => {
      if (data.success) {
        toast.success(t('toast.success.contentBlockCreated'))
        queryClient.invalidateQueries({ queryKey: ['content-blocks'] })
      } else {
        toast.error(data.error || t('toast.error.creating'))
      }
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}

/**
 * Hook for updating a content block (creates new version)
 */
export function useUpdateContentBlock(blockId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (data: UpdateContentBlockRequest) => updateContentBlock(blockId, data),
    onSuccess: (data) => {
      if (data.success) {
        toast.success(t('toast.success.contentBlockUpdated'))
        queryClient.invalidateQueries({ queryKey: ['content-blocks'] })
        queryClient.invalidateQueries({ queryKey: ['content-block', blockId] })
        queryClient.invalidateQueries({ queryKey: ['versions', 'content_block', blockId] })
        // Channel packages may be outdated now
        queryClient.invalidateQueries({ queryKey: ['channel-packages'] })
      } else {
        toast.error(data.error || t('toast.error.updating'))
      }
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}

/**
 * Hook for deleting a content block
 */
export function useDeleteContentBlock() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (blockId: string) => deleteContentBlock(blockId),
    onSuccess: (data) => {
      if (data.success) {
        toast.success(t('toast.success.contentBlockDeleted'))
        queryClient.invalidateQueries({ queryKey: ['content-blocks'] })
      } else {
        toast.error(data.error || t('toast.error.deleting'))
      }
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}

/**
 * Get block type display info
 */
export function getBlockTypeInfo(blockType: BlockType): {
  label: string
  icon: string
  description: string
} {
  switch (blockType) {
    case 'title':
      return {
        label: 'Titel',
        icon: 'bi-type-h1',
        description: 'Produkttitel',
      }
    case 'description':
      return {
        label: 'Beschreibung',
        icon: 'bi-text-paragraph',
        description: 'Ausführliche Produktbeschreibung',
      }
    case 'short_description':
      return {
        label: 'Kurzbeschreibung',
        icon: 'bi-text-left',
        description: 'Kurze Produktzusammenfassung',
      }
    case 'bullets':
      return {
        label: 'Bullet Points',
        icon: 'bi-list-ul',
        description: 'Aufzählung der wichtigsten Features',
      }
    case 'seo_meta':
      return {
        label: 'SEO Meta',
        icon: 'bi-search',
        description: 'SEO-Titel und Meta-Beschreibung',
      }
    default:
      return {
        label: blockType,
        icon: 'bi-file-text',
        description: '',
      }
  }
}

/**
 * Extract text from multi-lingual content
 * New format: {"de": "Text", "en": "Text"}
 * Old format: "Text" or {"text": "Text", "languageIso": "de"}
 */
function extractTextFromContent(content: unknown, preferredLang = 'de'): string {
  if (!content) return ''
  if (typeof content === 'string') return content

  if (typeof content === 'object' && content !== null) {
    const obj = content as Record<string, unknown>

    // New format: {"de": "...", "en": "..."}
    if (preferredLang in obj && typeof obj[preferredLang] === 'string') {
      return obj[preferredLang] as string
    }

    // Try other common languages
    const fallbackLangs = ['de', 'en', 'ger', 'eng', 'fr', 'fre']
    for (const lang of fallbackLangs) {
      if (lang in obj && typeof obj[lang] === 'string') {
        return obj[lang] as string
      }
    }

    // Old format: {"text": "...", "languageIso": "de"}
    if ('text' in obj && typeof obj.text === 'string') {
      return obj.text as string
    }
  }

  return JSON.stringify(content)
}

/**
 * Format content for display based on block type
 */
export function formatContentForDisplay(
  content: unknown,
  blockType: BlockType
): string {
  if (!content) return ''

  switch (blockType) {
    case 'title':
    case 'description':
    case 'short_description':
      return extractTextFromContent(content)

    case 'bullets':
      if (Array.isArray(content)) {
        return content.map(b => `• ${b}`).join('\n')
      }
      return extractTextFromContent(content)

    case 'seo_meta':
      if (typeof content === 'object' && content !== null) {
        const obj = content as Record<string, unknown>

        // Multi-lingual SEO format: {"de": {"title": "...", "description": "..."}}
        const langs = ['de', 'en', 'ger', 'eng', 'fr', 'fre']
        for (const lang of langs) {
          if (lang in obj && typeof obj[lang] === 'object' && obj[lang] !== null) {
            const meta = obj[lang] as { title?: string; description?: string }
            return `Titel: ${meta.title || ''}\nBeschreibung: ${meta.description || ''}`
          }
        }

        // Old format: {"title": "...", "description": "..."}
        if ('title' in obj || 'description' in obj) {
          const meta = obj as { title?: string; description?: string }
          return `Titel: ${meta.title || ''}\nBeschreibung: ${meta.description || ''}`
        }
      }
      return JSON.stringify(content, null, 2)

    default:
      return extractTextFromContent(content)
  }
}

